// Privacy Policy for Sign This!
// Last updated: 2026-02-05

export const PRIVACY_LAST_UPDATED = '2026-02-13';

export const PRIVACY_POLICY = {
  en: `
# Privacy Policy
**Last updated: February 13, 2026**

## 1. INTRODUCTION

- At Loxia Labs LLC, a company registered in the United States (collectively and individually "Company", "We," "Us," or "Our"), we respect your privacy and are committed to protecting your personal data.

- The purpose of this Privacy Policy (hereinafter: "the Policy") is to explain the Company's practices regarding the privacy of users of Sign This! (hereinafter: "the Service"), including how we collect personal data, the purposes for which we use it, and how and with whom we may share it.

- This Policy forms an integral part of our Terms of Use. By using the Service, you agree to the terms of this Policy. If you do not agree with any part of this Policy, please do not use the Service.

- The Company may update this Policy from time to time. Updates will become effective once posted on the Service. Your continued use of the Service after any changes means you accept the updated Policy.

- You agree that any dispute arising from your use of the Service will be governed exclusively by the laws of the State of Delaware, United States, and adjudicated solely in the state and federal courts located in Delaware.

- The term "you" as used in this Policy refers to all users of the Company's services (including Publishers who create forms and Signers who sign documents), as well as the personal data and other information relating to those users.

- This Policy is governed by and interpreted in accordance with applicable United States federal and state privacy laws, as well as any other applicable privacy laws and regulations to the extent relevant to the Company's operations and your use of the Service (together: "Applicable Data Protection Laws").

## 2. COLLECTION OF PERSONAL DATA

### 2.1 Data Provided by Publishers

When Publishers create signature collection forms, we collect:

- **Account credentials:** Password for form management (stored only as a secure cryptographic hash - we never store plain text passwords)
- **Form data:** Document files (PDF or images), form titles, and field definitions you create
- **Encryption keys:** Cryptographic keys used for data protection are secured with your password and cannot be accessed by the Company

### 2.2 Data Provided by Signers

When Signers complete signature forms, the following data is collected and encrypted:

- **Form field data:** Information entered into form fields as defined by the Publisher (e.g., name, ID number, address, contact information)
- **Digital signatures:** Signature images drawn by Signers
- **Timestamp:** Date and time of signature submission

**Important:** All personal data provided by Signers is encrypted using industry-standard encryption methods before storage. Only the Publisher with the correct password can decrypt and access this data. The Company does not have access to decrypted Signer data.

### 2.3 Data Collected Automatically

When using the Service, we may automatically collect:

- **Technical data:** IP address, browser type, device information, operating system
- **Usage data:** Pages viewed, features used, timestamps of access
- **Statistical information:** Anonymous or aggregate information (such as device type, click stream, browsing duration) that does not personally identify you

### 2.4 Your Responsibilities

- If you provide or upload any personal data to the Service, you represent that the personal data you provide is accurate and complete, and that you are providing it for yourself or have legal authorization to provide it for another person.
- Publishers are solely responsible for ensuring they have proper consent and legal basis to collect personal data from Signers through their forms.
- You are not required to provide personal data, but certain features of the Service may not be available without it.

## 3. USE OF PERSONAL DATA

We may use personal data for the following purposes:

- **Service provision:** To provide, operate, and maintain the Service, including storing and processing signature data
- **Security:** To protect the security and integrity of the Service, prevent fraud, and protect your safety and the safety of other users
- **Communication:** To respond to your inquiries and provide customer support
- **Improvement:** To improve the Service, analyze usage patterns, and enhance user experience (using aggregated, non-identifying data)
- **Legal compliance:** To comply with legal obligations, respond to court orders, and protect our legal rights and legitimate business interests
- **Marketing:** With your express consent, to send you newsletters or marketing communications (see Section 6)

We may use your personal data, together with information from other users, for statistical and analytical purposes to improve our services, while protecting your privacy and minimizing the use of personal details where possible.

### 3.2 Legal Basis for Processing (GDPR)

For users in the European Economic Area, our legal bases for processing your personal data are:

- **Contractual necessity (Art. 6(1)(b)):** Processing necessary to provide the Service, including storing forms, processing signatures, and managing your account
- **Legitimate interest (Art. 6(1)(f)):** Processing for security, fraud prevention, service improvement, and anonymous analytics
- **Consent (Art. 6(1)(a)):** For marketing communications and optional cookie usage
- **Legal obligation (Art. 6(1)(c)):** Where required by applicable law

## 4. SHARING PERSONAL DATA WITH THIRD PARTIES

The Company will not share your personal data with third parties except in the following cases:

- **With Publishers:** Signer data is shared with the Publisher who created the form (in encrypted form that only the Publisher can decrypt with their password)
- **Service providers (Sub-processors):** With the following technology service providers who process data on our behalf under strict confidentiality agreements:
  - **Microsoft Azure** (Microsoft Corporation, USA) – Cloud hosting, blob storage, table storage, and application infrastructure
  - **Stripe** (Stripe, Inc., USA) – Payment processing. Stripe is PCI DSS Level 1 certified. We never store, process, or transmit credit card data; all payment data is handled directly by Stripe
  - **Google** (Google LLC, USA) – OAuth authentication services
  - **Microsoft** (Microsoft Corporation, USA) – OAuth authentication services
- **Legal requirements:** If required by Applicable Data Protection Laws, court order, or legal process
- **Protection of rights:** To protect the rights, property, or safety of the Company, our users, or third parties
- **Business transfers:** In connection with a merger, acquisition, or sale of all or part of the Company's assets, provided that the third party agrees to protect your personal data consistent with this Policy

We may share aggregated or de-identified information with third parties for analytics, research, or service improvement purposes. Aggregated or de-identified information does not identify individual users and is not considered personal data.

Some of the Company's service providers, including cloud storage providers, may be located outside of the United States. In such cases, the Company takes steps to ensure that your personal data is adequately protected, including by requiring that recipients maintain appropriate security and confidentiality measures and comply with Applicable Data Protection Laws.

## 5. USE OF COOKIES AND ANALYTICS TOOLS

- The Service may use cookies and similar technologies for proper operation, improving user experience, and statistical analysis.

- **Types of cookies:**
  - Essential cookies – required for basic operation of the Service
  - Functional cookies – to improve user experience (such as saving language preferences)
  - Statistical cookies – to collect anonymous usage data

- You can block or delete cookies through your browser settings; however, some Service features may not function properly if you do so.

- By using the Service, you consent to the use of cookies as described above.

## 6. MARKETING COMMUNICATIONS

- We will only send you newsletters or marketing communications with your express consent, in accordance with applicable laws including the CAN-SPAM Act.

- Marketing communications may be sent by email or other digital means.

- You may opt out of receiving marketing communications at any time by using the unsubscribe link in our messages or by contacting the Company directly.

## 7. MINORS

The Service is intended for individuals 18 years of age or older. We do not knowingly collect personal data from users under 18. If you are under 18, you must have your parent's or legal guardian's permission to use the Service and provide personal data. If we learn that we have collected personal data from a minor without proper consent, we will delete that information as soon as possible.

## 8. DATA RETENTION AND SECURITY

### 8.1 Retention

- Personal data will be stored only for as long as necessary to fulfill the purposes for which it was collected, to provide the Service, to resolve disputes, or as required by law.
- Publishers may delete their forms and associated signature data at any time.
- Instead of deleting personal data, the Company may de-identify or aggregate it so that it no longer identifies you.

### 8.2 Security Measures

The Company implements robust security measures to protect personal data, including:

- **Encryption at rest:** All Signer personal data is encrypted using industry-standard cryptographic methods before storage
- **Password protection:** Publisher passwords are securely hashed; decryption keys are protected and inaccessible without the correct password
- **Encrypted transmission:** All data is transmitted over secure, encrypted connections
- **Access controls:** Only the Publisher with the correct password can access and decrypt Signer data
- **Zero-knowledge architecture:** The Company does not have access to decrypted Signer data or Publisher passwords

However, no method of transmission or storage is completely secure. The Company is not responsible for unauthorized access or use of your personal data if it has taken all reasonable and legally required measures and the breach was caused by circumstances beyond its control.

### 8.3 Third-Party Vendors

When transferring personal data to third parties as described in Section 4, the Company uses vendors who maintain high standards of security and privacy protection. Only the personal data necessary to perform the requested services is shared with these external suppliers, subject to their agreement to maintain confidentiality and comply with our legal obligations.

## 9. DATABASES

Personal data collected will be stored in the Company's databases and under its responsibility, subject to applicable United States federal and state privacy and data protection laws.

## 10. LINKS TO EXTERNAL WEBSITES

The Service may include links to external websites or services. Your use of these services is subject to the privacy policies of those third parties, and the Company is not responsible for their practices.

## 11. DATA SUBJECT RIGHTS

In accordance with the Privacy Protection Law and other Applicable Data Protection Laws:

### 11.1 Publisher Rights

Publishers can:
- Access all collected signature data (after password authentication)
- Export signature data in various formats (PDF, Excel)
- Delete forms and all associated signature data
- Request information about their stored personal data

### 11.2 Signer Rights

Signers should contact the Publisher who collected their signature to:
- Request access to their data
- Request correction of inaccurate data
- Request deletion of their data

If you cannot reach the Publisher, you may contact us and we will assist in forwarding your request where technically feasible.

### 11.3 General Rights

- You have the right to request access to the personal data we hold about you.
- You have the right to request correction or deletion of your personal data if it is inaccurate, incomplete, or outdated.
- Under certain circumstances and jurisdictions (including GDPR), you may have additional rights such as data portability, restriction of processing, and the right to object.
- Requests should be directed to: contact@loxia.ai. The Company will respond to your request as soon as possible and no later than 30 days from the date of receipt, in accordance with legal requirements.

## 12. CALIFORNIA RESIDENTS (CCPA)

If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):

- **Right to Know:** You can request details about the personal information we collect, use, and disclose.
- **Right to Delete:** You can request deletion of your personal information, subject to certain exceptions.
- **Right to Opt-Out:** We do not sell your personal information to third parties. **We do not sell personal data.**
- **Non-Discrimination:** We will not discriminate against you for exercising your CCPA rights.

To exercise these rights, contact us at contact@loxia.ai or use the account deletion and data export features in your account settings.

## 13. PAYMENT PROCESSING

All payment processing is handled by **Stripe, Inc.**, which is certified as a **PCI DSS Level 1 Service Provider** — the highest level of security certification in the payments industry. We never store, process, or have access to your full credit card numbers. All payment data is transmitted directly to Stripe over encrypted connections.

## 14. LIMITATION OF LIABILITY

- The Company is not responsible for the actions of Publishers regarding the Signer data they collect, including how they use, store, or share such data after export.
- The Company does not guarantee the legal validity or enforceability of signatures collected through the Service.
- Publishers are solely responsible for compliance with Applicable Data Protection Laws regarding their collection and use of Signer data.

## 15. CONTACT US

For questions or requests regarding your personal data or this Policy, you can contact us at:

**Email:** contact@loxia.ai

**Data Protection Officer:** daniel.suissa@loxia.ai

**Address:** Loxia Labs LLC, United States
`,

  he: `
# מדיניות פרטיות
**עודכנה לאחרונה: 13 בפברואר 2026**

## 1. מבוא

- ב-Loxia Labs LLC, חברה רשומה בארצות הברית (ביחד וכל אחד בנפרד "החברה", "אנחנו" או "שלנו"), אנו מכבדים את פרטיותכם ומחויבים להגן על המידע האישי שלכם.

- מטרת מדיניות פרטיות זו (להלן: "המדיניות") היא להסביר את הנהלים של החברה בנוגע לפרטיות משתמשי Sign This! (להלן: "השירות"), כולל כיצד אנו אוספים מידע אישי, המטרות שלשמן אנו משתמשים בו, וכיצד ועם מי אנו עשויים לשתף אותו.

- מדיניות זו מהווה חלק בלתי נפרד מתנאי השימוש שלנו. על ידי שימוש בשירות, אתם מסכימים לתנאי מדיניות זו. אם אינכם מסכימים לחלק כלשהו ממדיניות זו, אנא אל תשתמשו בשירות.

- החברה רשאית לעדכן מדיניות זו מעת לעת. עדכונים ייכנסו לתוקף עם פרסומם בשירות. המשך השימוש שלכם בשירות לאחר שינויים כלשהם מעיד על הסכמתכם למדיניות המעודכנת.

- אתם מסכימים שכל מחלוקת הנובעת משימושכם בשירות תהיה כפופה באופן בלעדי לחוקי מדינת דלאוור, ארצות הברית, ותידון אך ורק בבתי המשפט המדינתיים והפדרליים בדלאוור.

- המונח "אתם" כפי שמשמש במדיניות זו מתייחס לכל משתמשי שירותי החברה (כולל מפרסמים היוצרים טפסים וחותמים החותמים על מסמכים), כמו גם למידע האישי ומידע אחר הנוגע למשתמשים אלה.

- מדיניות זו כפופה ומפורשת בהתאם לחוקי הפרטיות והגנת המידע הפדרליים והמדינתיים החלים בארצות הברית, כמו גם כל חוקי פרטיות אחרים החלים במידה הרלוונטית לפעילות החברה ולשימושכם בשירות (יחד: "דיני הגנת מידע החלים").

## 2. איסוף מידע אישי

### 2.1 מידע שמפרסמים מספקים

כאשר מפרסמים יוצרים טפסי איסוף חתימות, אנו אוספים:

- **אישורי חשבון:** סיסמה לניהול הטופס (מאוחסנת רק כגיבוב קריפטוגרפי מאובטח - אנחנו אף פעם לא מאחסנים סיסמאות בטקסט רגיל)
- **נתוני טופס:** קבצי מסמכים (PDF או תמונות), כותרות טפסים והגדרות שדות שאתם יוצרים
- **מפתחות הצפנה:** מפתחות קריפטוגרפיים המשמשים להגנת נתונים מאובטחים עם הסיסמה שלכם ואינם נגישים לחברה

### 2.2 מידע שחותמים מספקים

כאשר חותמים משלימים טפסי חתימה, המידע הבא נאסף ומוצפן:

- **נתוני שדות טופס:** מידע שהוזן לשדות הטופס כפי שהוגדרו על ידי המפרסם (כגון שם, מספר תעודת זהות, כתובת, פרטי קשר)
- **חתימות דיגיטליות:** תמונות חתימה שצוירו על ידי חותמים
- **חותמת זמן:** תאריך ושעת שליחת החתימה

**חשוב:** כל המידע האישי שחותמים מספקים מוצפן באמצעות שיטות הצפנה תקניות לפני האחסון. רק המפרסם עם הסיסמה הנכונה יכול לפענח ולגשת למידע זה. לחברה אין גישה למידע מפוענח של חותמים.

### 2.3 מידע שנאסף אוטומטית

בעת שימוש בשירות, אנו עשויים לאסוף באופן אוטומטי:

- **מידע טכני:** כתובת IP, סוג דפדפן, מידע על מכשיר, מערכת הפעלה
- **נתוני שימוש:** דפים שנצפו, תכונות שהופעלו, חותמות זמן של גישה
- **מידע סטטיסטי:** מידע אנונימי או מצטבר (כגון סוג מכשיר, רצף לחיצות, משך גלישה) שאינו מזהה אתכם באופן אישי

### 2.4 אחריותכם

- אם אתם מספקים או מעלים מידע אישי כלשהו לשירות, אתם מצהירים שהמידע האישי שאתם מספקים מדויק ומלא, ושאתם מספקים אותו עבור עצמכם או שיש לכם הרשאה חוקית לספק אותו עבור אדם אחר.
- מפרסמים אחראים באופן בלעדי להבטחה שיש להם הסכמה נאותה ובסיס חוקי לאסוף מידע אישי מחותמים דרך הטפסים שלהם.
- אינכם נדרשים לספק מידע אישי, אך תכונות מסוימות של השירות עשויות שלא להיות זמינות בלעדיו.

## 3. שימוש במידע אישי

אנו עשויים להשתמש במידע אישי למטרות הבאות:

- **מתן שירות:** לספק, להפעיל ולתחזק את השירות, כולל אחסון ועיבוד נתוני חתימות
- **אבטחה:** להגן על אבטחת ושלמות השירות, למנוע הונאה ולהגן על בטיחותכם ובטיחות משתמשים אחרים
- **תקשורת:** להשיב לפניותיכם ולספק תמיכת לקוחות
- **שיפור:** לשפר את השירות, לנתח דפוסי שימוש ולשפר את חוויית המשתמש (באמצעות נתונים מצטברים שאינם מזהים)
- **ציות לחוק:** לציית לחובות משפטיות, להגיב לצווי בית משפט ולהגן על זכויותינו המשפטיות ואינטרסים עסקיים לגיטימיים
- **שיווק:** עם הסכמתכם המפורשת, לשלוח לכם ניוזלטרים או תקשורת שיווקית (ראו סעיף 6)

אנו עשויים להשתמש במידע האישי שלכם, יחד עם מידע ממשתמשים אחרים, למטרות סטטיסטיות ואנליטיות לשיפור השירותים שלנו, תוך הגנה על פרטיותכם ומזעור השימוש בפרטים אישיים היכן שאפשר.

### 3.2 בסיס חוקי לעיבוד (GDPR)

עבור משתמשים באזור הכלכלי האירופי, הבסיסים החוקיים שלנו לעיבוד המידע האישי שלכם הם:

- **הכרח חוזי (סעיף 6(1)(ב)):** עיבוד הנדרש לאספקת השירות, כולל אחסון טפסים, עיבוד חתימות וניהול החשבון שלכם
- **אינטרס לגיטימי (סעיף 6(1)(ו)):** עיבוד לצורכי אבטחה, מניעת הונאה, שיפור השירות וניתוח אנונימי
- **הסכמה (סעיף 6(1)(א)):** לתקשורת שיווקית ושימוש אופציונלי בעוגיות
- **חובה חוקית (סעיף 6(1)(ג)):** היכן שנדרש על פי חוק

## 4. שיתוף מידע אישי עם צדדים שלישיים

החברה לא תשתף את המידע האישי שלכם עם צדדים שלישיים למעט במקרים הבאים:

- **עם מפרסמים:** מידע חותמים משותף עם המפרסם שיצר את הטופס (בצורה מוצפנת שרק המפרסם יכול לפענח עם הסיסמה שלו)
- **ספקי שירות (מעבדי משנה):** עם ספקי שירותי הטכנולוגיה הבאים שמעבדים נתונים בשמנו תחת הסכמי סודיות מחמירים:
  - **Microsoft Azure** (Microsoft Corporation, ארה"ב) – אירוח בענן, אחסון blob, אחסון טבלאות ותשתית אפליקציה
  - **Stripe** (Stripe, Inc., ארה"ב) – עיבוד תשלומים. Stripe מאושרת PCI DSS Level 1. אנחנו לעולם לא מאחסנים, מעבדים או מעבירים נתוני כרטיסי אשראי; כל נתוני התשלום מטופלים ישירות על ידי Stripe
  - **Google** (Google LLC, ארה"ב) – שירותי אימות OAuth
  - **Microsoft** (Microsoft Corporation, ארה"ב) – שירותי אימות OAuth
- **דרישות משפטיות:** אם נדרש על פי דיני הגנת מידע החלים, צו בית משפט או הליך משפטי
- **הגנה על זכויות:** להגן על זכויות, רכוש או בטיחות של החברה, משתמשינו או צדדים שלישיים
- **העברות עסקיות:** בקשר למיזוג, רכישה או מכירה של כל נכסי החברה או חלקם, בתנאי שהצד השלישי מסכים להגן על המידע האישי שלכם בהתאם למדיניות זו

אנו עשויים לשתף מידע מצטבר או שעבר הסרת זיהוי עם צדדים שלישיים למטרות ניתוח, מחקר או שיפור שירות. מידע מצטבר או שעבר הסרת זיהוי אינו מזהה משתמשים בודדים ואינו נחשב למידע אישי.

חלק מספקי השירות של החברה, כולל ספקי אחסון בענן, עשויים להימצא מחוץ לארצות הברית. במקרים כאלה, החברה נוקטת צעדים להבטיח שהמידע האישי שלכם מוגן כראוי, כולל דרישה שמקבלי המידע ישמרו על אמצעי אבטחה וסודיות מתאימים ויצייתו לדיני הגנת מידע החלים.

## 5. שימוש בעוגיות וכלי ניתוח

- השירות עשוי להשתמש בעוגיות וטכנולוגיות דומות לתפעול תקין, שיפור חוויית המשתמש וניתוח סטטיסטי.

- **סוגי עוגיות:**
  - עוגיות חיוניות – נדרשות לתפעול בסיסי של השירות
  - עוגיות פונקציונליות – לשיפור חוויית המשתמש (כגון שמירת העדפות שפה)
  - עוגיות סטטיסטיות – לאיסוף נתוני שימוש אנונימיים

- תוכלו לחסום או למחוק עוגיות דרך הגדרות הדפדפן שלכם; עם זאת, תכונות מסוימות של השירות עשויות שלא לפעול כראוי אם תעשו זאת.

- על ידי שימוש בשירות, אתם מסכימים לשימוש בעוגיות כמתואר לעיל.

## 6. תקשורת שיווקית

- נשלח לכם ניוזלטרים או תקשורת שיווקית רק עם הסכמתכם המפורשת, בהתאם לחוקים החלים כולל חוק CAN-SPAM.

- תקשורת שיווקית עשויה להישלח באימייל או באמצעים דיגיטליים אחרים.

- תוכלו לבטל את הרשמתכם לתקשורת שיווקית בכל עת באמצעות קישור ביטול ההרשמה בהודעותינו או על ידי פנייה ישירה לחברה.

## 7. קטינים

השירות מיועד לאנשים בני 18 ומעלה. אנחנו לא אוספים ביודעין מידע אישי ממשתמשים מתחת לגיל 18. אם אתם מתחת לגיל 18, עליכם לקבל את אישור ההורה או האפוטרופוס החוקי שלכם כדי להשתמש בשירות ולספק מידע אישי. אם נגלה שאספנו מידע אישי מקטין ללא הסכמה נאותה, נמחק מידע זה בהקדם האפשרי.

## 8. שמירת מידע ואבטחה

### 8.1 שמירה

- מידע אישי יישמר רק כל עוד יש צורך בכך למימוש המטרות שלשמן נאסף, לאספקת השירות, ליישוב מחלוקות או כנדרש על פי חוק.
- מפרסמים יכולים למחוק את הטפסים שלהם ונתוני החתימות הקשורים בכל עת.
- במקום למחוק מידע אישי, החברה עשויה להסיר את הזיהוי ממנו או לצבור אותו כך שלא יזהה אתכם עוד.

### 8.2 אמצעי אבטחה

החברה מיישמת אמצעי אבטחה חזקים להגנה על מידע אישי, כולל:

- **הצפנה במנוחה:** כל המידע האישי של חותמים מוצפן באמצעות שיטות קריפטוגרפיות תקניות לפני האחסון
- **הגנת סיסמה:** סיסמאות מפרסמים עוברות גיבוב מאובטח; מפתחות פענוח מוגנים ואינם נגישים ללא הסיסמה הנכונה
- **העברה מוצפנת:** כל הנתונים מועברים באמצעות חיבורים מאובטחים ומוצפנים
- **בקרות גישה:** רק המפרסם עם הסיסמה הנכונה יכול לגשת ולפענח נתוני חותמים
- **ארכיטקטורת אפס ידע:** לחברה אין גישה למידע מפוענח של חותמים או לסיסמאות מפרסמים

עם זאת, אף שיטת העברה או אחסון אינה מאובטחת לחלוטין. החברה אינה אחראית לגישה או שימוש בלתי מורשים במידע האישי שלכם אם נקטה את כל האמצעים הסבירים והנדרשים על פי חוק והפרצה נגרמה בנסיבות שמחוץ לשליטתה.

### 8.3 ספקים חיצוניים

בעת העברת מידע אישי לצדדים שלישיים כמתואר בסעיף 4, החברה משתמשת בספקים השומרים על סטנדרטים גבוהים של אבטחה והגנת פרטיות. רק המידע האישי הנחוץ לביצוע השירותים המבוקשים משותף עם ספקים חיצוניים אלה, בכפוף להסכמתם לשמור על סודיות ולציית לחובותינו המשפטיות.

## 9. מאגרי מידע

מידע אישי שנאסף יישמר במאגרי הנתונים של החברה ובאחריותה, בכפוף לחוקי הפרטיות והגנת המידע הפדרליים והמדינתיים החלים בארצות הברית.

## 10. קישורים לאתרים חיצוניים

השירות עשוי לכלול קישורים לאתרים או שירותים חיצוניים. השימוש שלכם בשירותים אלה כפוף למדיניות הפרטיות של צדדים שלישיים אלה, והחברה אינה אחראית לנהליהם.

## 11. זכויות נושא המידע

בהתאם לחוק הגנת הפרטיות ודיני הגנת מידע אחרים החלים:

### 11.1 זכויות מפרסמים

מפרסמים יכולים:
- לגשת לכל נתוני החתימות שנאספו (לאחר אימות סיסמה)
- לייצא נתוני חתימות בפורמטים שונים (PDF, Excel)
- למחוק טפסים וכל נתוני החתימות הקשורים
- לבקש מידע על המידע האישי המאוחסן שלהם

### 11.2 זכויות חותמים

חותמים צריכים לפנות למפרסם שאסף את חתימתם כדי:
- לבקש גישה למידע שלהם
- לבקש תיקון של מידע לא מדויק
- לבקש מחיקת המידע שלהם

אם אינכם יכולים להגיע למפרסם, תוכלו לפנות אלינו ואנו נסייע בהעברת בקשתכם היכן שזה אפשרי טכנית.

### 11.3 זכויות כלליות

- יש לכם זכות לבקש גישה למידע האישי שאנו מחזיקים עליכם.
- יש לכם זכות לבקש תיקון או מחיקה של המידע האישי שלכם אם הוא לא מדויק, לא שלם או לא עדכני.
- בנסיבות מסוימות ובתחומי שיפוט מסוימים (כולל GDPR), עשויות להיות לכם זכויות נוספות כגון ניידות מידע, הגבלת עיבוד וזכות להתנגד.
- בקשות יש להפנות אל: contact@loxia.ai. החברה תשיב לבקשתכם בהקדם האפשרי ולא יאוחר מ-30 יום ממועד הקבלה, בהתאם לדרישות החוק.

## 12. תושבי קליפורניה (CCPA)

אם אתם תושבי קליפורניה, יש לכם זכויות נוספות תחת חוק הפרטיות של צרכני קליפורניה (CCPA):

- **זכות לדעת:** תוכלו לבקש פרטים על המידע האישי שאנו אוספים, משתמשים בו וחושפים.
- **זכות למחיקה:** תוכלו לבקש מחיקת המידע האישי שלכם, בכפוף לחריגים מסוימים.
- **זכות לסרב:** אנחנו לא מוכרים את המידע האישי שלכם לצדדים שלישיים. **אנחנו לא מוכרים מידע אישי.**
- **אי-אפליה:** לא נפלה לרעה נגדכם בגין מימוש זכויותיכם תחת CCPA.

למימוש זכויות אלה, פנו אלינו בכתובת contact@loxia.ai או השתמשו בתכונות מחיקת חשבון וייצוא מידע בהגדרות החשבון שלכם.

## 13. עיבוד תשלומים

כל עיבוד התשלומים מתבצע על ידי **Stripe, Inc.**, המאושרת כ-**PCI DSS Level 1 Service Provider** — רמת אישור האבטחה הגבוהה ביותר בתעשיית התשלומים. אנחנו לעולם לא מאחסנים, מעבדים או ניגשים למספרי כרטיסי האשראי המלאים שלכם. כל נתוני התשלום מועברים ישירות ל-Stripe דרך חיבורים מוצפנים.

## 14. הגבלת אחריות

- החברה אינה אחראית לפעולות מפרסמים בנוגע למידע החותמים שהם אוספים, כולל כיצד הם משתמשים, מאחסנים או משתפים מידע זה לאחר הייצוא.
- החברה אינה מבטיחה את התוקף המשפטי או האכיפות של חתימות שנאספו דרך השירות.
- מפרסמים אחראים באופן בלעדי לציות לדיני הגנת מידע החלים בנוגע לאיסוף ושימוש במידע חותמים.

## 15. צרו קשר

לשאלות או בקשות בנוגע למידע האישי שלכם או למדיניות זו, תוכלו לפנות אלינו:

**אימייל:** contact@loxia.ai

**קצין הגנת מידע:** daniel.suissa@loxia.ai

**כתובת:** Loxia Labs LLC, ארצות הברית
`
};
